import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Form, Button, Col } from 'react-bootstrap';
import { includes, isEqual, map } from 'lodash';
import faker from 'faker';

import * as templateOps from '../../../api/AnnotationTemplates/annotation-template-operations';
import {
  ELEMENT_TYPES,
  LABEL_TYPES,
  OPTION_LIST_TYPES
} from '../../../api/AnnotationTemplates/AnnotationTemplates';

import Validation from '../../containers/Validation';

const EXAMPLE_MARKDOWN = `### Example Text Area

You can even use markdown to do some styling:

* Implements [GitHub Flavored Markdown](https://github.github.com/gfm/)

Give it a try.
`;
const EXAMPLE_NUMBER_MAX_RANGE = 100;
const EXAMPLE_DATE_DAYS_AGO = 100;
const EXAMPLE_VIDEO_URL = 'https://www.youtube.com/watch?v=yHyMR7T6Kpk&frags=pl%2Cwn';
const EXAMPLE_IMAGE_URL = 'https://loremflickr.com/640/480/business,meeting,formal/all';
const EXAMPLE_LINK_URL = 'https://srmconsulting.es/es/';
const EXAMPLE_ICON_KEY = 'fighter-jet';

const CommonElementData = ({ templateElement, handleElementTypeChange }) => (
  <>
    <Form.Group>
      <Form.Label>Row</Form.Label>
      <Form.Control
        type="number"
        name="row"
        defaultValue={templateOps.getElementRow(templateElement)}
        placeholder="The row for your element"
      />
    </Form.Group>
    <Form.Group>
      <Form.Label>Column</Form.Label>
      <Form.Control
        type="number"
        name="column"
        defaultValue={templateOps.getElementColumn(templateElement)}
        placeholder="The column for your element"
      />
    </Form.Group>
    <Form.Group>
      <Form.Label>Order</Form.Label>
      <Form.Control
        type="number"
        name="order"
        defaultValue={templateOps.getElementOrder(templateElement)}
        placeholder="The order for your element inside the column"
      />
    </Form.Group>
    <Form.Group>
      <Form.Label>Element Key</Form.Label>
      <Form.Control
        type="text"
        name="elementKey"
        defaultValue={templateOps.getElementKey(templateElement)}
        placeholder="Unique identifier for the element"
      />
    </Form.Group>
    <Form.Group>
      <Form.Label>Element Type</Form.Label>
      <Form.Control
        as="select"
        name="elementType"
        defaultValue={templateOps.getElementType(templateElement)}
        onChange={handleElementTypeChange}
      >
        {map(ELEMENT_TYPES.properties, ({ value, humanValue }) => (
          <option value={value} key={value}>
            {humanValue}
          </option>
        ))}
      </Form.Control>
    </Form.Group>
  </>
);

CommonElementData.propTypes = {
  templateElement: PropTypes.object.isRequired,
  handleElementTypeChange: PropTypes.func.isRequired
};

const ExtraElementData = ({ elementType, templateElement }) => {
  const extraElementData = [];
  if (includes(LABEL_TYPES, elementType)) {
    extraElementData.push(
      <React.Fragment key="labelName">
        <Form.Group>
          <Form.Label>Label Name</Form.Label>
          <Form.Control
            type="text"
            name="labelName"
            defaultValue={templateOps.getElementLabelName(templateElement)}
            placeholder="The name of the label"
          />
        </Form.Group>
      </React.Fragment>
    );
  }
  if (ELEMENT_TYPES.INPUT === elementType) {
    extraElementData.push(
      <React.Fragment key="dataType">
        <Form.Group>
          <Form.Label>Data Type</Form.Label>
          <Form.Control
            as="select"
            name="dataType"
            defaultValue={templateOps.getElementDataType(templateElement)}
          >
            <option value="text">Text</option>
            <option value="number">Number</option>
            <option value="date">Date</option>
          </Form.Control>
        </Form.Group>
      </React.Fragment>
    );
  }
  if (includes(OPTION_LIST_TYPES, elementType)) {
    extraElementData.push(
      <React.Fragment key="optionList">
        <Form.Group>
          <Form.Label>Option List</Form.Label>
          <Form.Control
            type="text"
            name="optionList"
            defaultValue={templateOps.transformOptionsArrayToString(
              templateOps.getElementOptionList(templateElement)
            )}
            placeholder="Comma separated list"
          />
        </Form.Group>
      </React.Fragment>
    );
  }
  return extraElementData;
};

ExtraElementData.propTypes = {
  templateElement: PropTypes.object.isRequired,
  elementType: PropTypes.string.isRequired
};

class AnnotationTemplateElementEditor extends Component {
  state = {
    elementType: templateOps.getElementType(this.props.templateElement)
  };

  componentDidUpdate(prevProps) {
    const { templateElement: newTemplateElement } = this.props;
    const oldTemplateElement = prevProps.templateElement;
    if (!isEqual(newTemplateElement, oldTemplateElement))
      this.updateElementType(templateOps.getElementType(newTemplateElement));
  }

  updateElementType = newType => this.setState({ elementType: newType });

  handleElementTypeChange = event => this.setState({ elementType: event.target.value.trim() });

  handleSubmit = elementForm => {
    const { selectedElementIndex, handleElementUpdate, handleElementAddition } = this.props;
    const { elementType } = this.state;
    const existingTemplateElement = selectedElementIndex >= 0;

    const newTemplateElement = {
      row: Number(elementForm.row.value.trim()),
      column: Number(elementForm.column.value.trim()),
      order: Number(elementForm.order.value.trim()),
      elementKey: elementForm.elementKey.value.trim(),
      elementType: elementForm.elementType.value.trim()
    };

    switch (elementType) {
      case ELEMENT_TYPES.INPUT:
        newTemplateElement.labelName = elementForm.labelName.value.trim();
        newTemplateElement.dataType = elementForm.dataType.value.trim();
        if (templateOps.getElementDataType(newTemplateElement) === 'date')
          newTemplateElement.value = faker.date
            .recent(EXAMPLE_DATE_DAYS_AGO)
            .toISOString()
            .slice(0, 10);
        else if (templateOps.getElementDataType(newTemplateElement) === 'number')
          newTemplateElement.value = faker.datatype.number(EXAMPLE_NUMBER_MAX_RANGE).toString();
        else newTemplateElement.value = faker.company.catchPhrase();
        break;

      case ELEMENT_TYPES.TEXTAREA:
        newTemplateElement.labelName = elementForm.labelName.value.trim();
        newTemplateElement.value = EXAMPLE_MARKDOWN;
        break;

      case ELEMENT_TYPES.SELECT:
      case ELEMENT_TYPES.RADIO:
        newTemplateElement.labelName = elementForm.labelName.value.trim();
        newTemplateElement.optionList = templateOps.transformOptionsStringToArray(
          elementForm.optionList.value.trim()
        );
        [newTemplateElement.value] = templateOps.transformOptionsStringToArray(
          elementForm.optionList.value.trim()
        );
        break;

      case ELEMENT_TYPES.IMAGE:
        newTemplateElement.caption = faker.company.catchPhrase();
        newTemplateElement.value = EXAMPLE_IMAGE_URL;
        break;

      case ELEMENT_TYPES.VIDEO:
        newTemplateElement.caption = faker.company.catchPhrase();
        newTemplateElement.value = EXAMPLE_VIDEO_URL;
        break;

      case ELEMENT_TYPES.LINK:
      case ELEMENT_TYPES.IFRAME:
        newTemplateElement.caption = faker.company.catchPhrase();
        newTemplateElement.value = EXAMPLE_LINK_URL;
        break;

      case ELEMENT_TYPES.ICON:
        newTemplateElement.caption = faker.company.catchPhrase();
        newTemplateElement.value = EXAMPLE_LINK_URL;
        newTemplateElement.iconKey = EXAMPLE_ICON_KEY;
        break;
      case ELEMENT_TYPES.BUTTON:
        newTemplateElement.value = 'Open Link';
        break;

      default:
        break;
    }

    if (existingTemplateElement) handleElementUpdate(newTemplateElement, selectedElementIndex);
    else handleElementAddition(newTemplateElement);
  };

  render() {
    const { templateElement, selectedElementIndex, cancelEditElement } = this.props;
    const { elementType } = this.state;
    const elementKey = `${selectedElementIndex}${templateOps.getElementRow(
      templateElement
    )}${templateOps.getElementColumn(templateElement)}${templateOps.getElementOrder(
      templateElement
    )}`;
    const validationProps = {
      rules: {
        row: {
          required: true,
          min: 1
        },
        column: {
          required: true,
          min: 1
        },
        order: {
          required: true,
          min: 1
        },
        elementKey: {
          required: true
        },
        elementType: {
          required: true
        },
        labelName: {
          required: includes(LABEL_TYPES, elementType)
        },
        dataType: {
          required: elementType === ELEMENT_TYPES.INPUT
        },
        optionList: {
          required: includes(OPTION_LIST_TYPES, elementType)
        }
      },
      messages: {
        row: {
          required: 'You need a row for the element',
          min: 'This number needs to be greater than 0'
        },
        column: {
          required: 'You need a column for the element',
          min: 'This number needs to be greater than 0'
        },
        order: {
          required: 'You need an order for the element',
          min: 'This number needs to be greater than 0'
        },
        elementKey: {
          required: 'You need a unique identifier for the element'
        },
        elementType: {
          required: 'You need a type for the element'
        },
        labelName: {
          required: 'You need a name for the input label'
        },
        dataType: {
          required: 'You need a dta type for the input field'
        },
        optionList: {
          required: 'You need options for the field'
        }
      },
      submitHandler: form => this.handleSubmit(form)
    };
    return (
      <Validation {...validationProps}>
        <Form onSubmit={event => event.preventDefault()}>
          <Form.Row>
            <Col xs={12}>
              <CommonElementData
                templateElement={templateElement}
                handleElementTypeChange={this.handleElementTypeChange}
                key={`common-${elementKey}`}
              />
              <ExtraElementData
                templateElement={templateElement}
                elementType={elementType}
                key={`extra-${elementKey}`}
              />
            </Col>
            <Col xs={12} className="d-flex justify-content-end">
              <Button variant="danger" onClick={cancelEditElement}>
                Cancel
              </Button>
              <Button type="submit" variant="success">
                {selectedElementIndex >= 0 ? 'Save Changes' : 'Add Element'}
              </Button>
            </Col>
          </Form.Row>
        </Form>
      </Validation>
    );
  }
}

AnnotationTemplateElementEditor.defaultProps = {
  templateElement: {
    row: '',
    column: '',
    order: '',
    elementKey: '',
    elementType: 'input',
    labelName: '',
    dataType: '',
    optionList: [],
    caption: '',
    iconKey: ''
  }
};

AnnotationTemplateElementEditor.propTypes = {
  templateElement: PropTypes.object,
  selectedElementIndex: PropTypes.number.isRequired,
  handleElementAddition: PropTypes.func.isRequired,
  handleElementUpdate: PropTypes.func.isRequired,
  cancelEditElement: PropTypes.func.isRequired
};

export default AnnotationTemplateElementEditor;
